import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import StatusPill from "@/components/StatusPill";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, ArrowUpRight, Package, ClockClockwise } from "@phosphor-icons/react";

function Kpi({ label, value, testid, tone = "default" }) {
  return (
    <div data-testid={testid} className="border border-border p-6 bg-white">
      <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">{label}</div>
      <div className={`font-heading font-black text-4xl tracking-tighter ${tone === "primary" ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [r, o] = await Promise.all([api.get("/requests"), api.get("/orders")]);
        setRequests(r.data.items || []);
        setOrders(o.data.items || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const active = requests.filter(r => !["completed", "delivered", "declined", "cancelled"].includes(r.status));
  const quotesAwaiting = requests.filter(r => ["estimate_ready", "revised_quote_ready", "awaiting_decision", "awaiting_payment"].includes(r.status));
  const inProduction = orders.filter(o => ["paid", "in_production", "quality_review"].includes(o.status));
  const completed = orders.filter(o => ["delivered", "completed"].includes(o.status));

  return (
    <div className="App min-h-screen">
      <Navbar />
      <section className="border-b border-border py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-2">Dashboard</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl md:text-5xl">Welcome back, {user?.name?.split(" ")[0] || "there"}.</h1>
          </div>
          <Link to="/new-request">
            <Button data-testid={TID.dashboard.newBtn} size="lg" className="rounded-sm h-11 px-5">
              <Plus size={16} className="mr-2" /> New audience request
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi testid={TID.dashboard.kpiActive} label="Active requests" value={active.length} tone="primary" />
          <Kpi testid={TID.dashboard.kpiQuotes} label="Quotes awaiting" value={quotesAwaiting.length} />
          <Kpi testid={TID.dashboard.kpiOrders} label="In production" value={inProduction.length} />
          <Kpi testid={TID.dashboard.kpiCompleted} label="Completed" value={completed.length} />
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading font-bold text-2xl">Your requests</h2>
              <Link to="/new-request" className="text-sm font-medium hover:underline flex items-center gap-1">New <Plus size={14} /></Link>
            </div>
            {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
            {!loading && requests.length === 0 && (
              <div className="border border-dashed border-border p-10 text-center">
                <ClockClockwise size={28} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
                <div className="font-semibold mb-1">No requests yet</div>
                <p className="text-sm text-muted-foreground mb-4">Describe an audience to get an estimate and a 20-row sample.</p>
                <Link to="/new-request"><Button className="rounded-sm">Create request</Button></Link>
              </div>
            )}
            <div className="space-y-3">
              {requests.map((r) => (
                <Link key={r.request_id} to={`/requests/${r.request_id}`} data-testid={`${TID.dashboard.reqCard}-${r.request_id}`} className="block border border-border p-5 hover:border-foreground transition-colors bg-white">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-[11px] text-muted-foreground">{r.reference}</span>
                        <StatusPill status={r.status} />
                      </div>
                      <div className="font-semibold text-base truncate">{r.title}</div>
                      <div className="text-xs text-muted-foreground mt-2 flex gap-4 flex-wrap">
                        {r.estimated_volume ? <span>Est. audience: <b className="text-foreground">{r.estimated_volume.toLocaleString()}</b></span> : null}
                        <span>Created: {new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <ArrowUpRight size={18} className="text-muted-foreground shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <aside>
            <h2 className="font-heading font-bold text-2xl mb-4">Recent orders</h2>
            {orders.length === 0 && <div className="text-sm text-muted-foreground">No orders yet.</div>}
            <div className="space-y-3">
              {orders.slice(0, 6).map((o) => (
                <Link key={o.order_id} to={`/orders/${o.order_id}`} data-testid={`${TID.dashboard.orderCard}-${o.order_id}`} className="block border border-border p-4 hover:border-foreground bg-white">
                  <div className="flex items-center gap-3 mb-1">
                    <Package size={14} className="text-muted-foreground" />
                    <span className="font-mono text-[11px] text-muted-foreground">{o.reference}</span>
                    <StatusPill status={o.status} />
                  </div>
                  <div className="text-sm font-semibold">{o.currency} {o.amount?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  <div className="text-xs text-muted-foreground">Option: {o.option_key}</div>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
